import { observer } from 'mobx-react-lite'
import { lazy } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Status, StatusUIType } from 'sections/LKSection/shared/components/UI/Status/Status'
import { Margin, Padding } from 'components/ui'
import { Flex, Pagination, Search, Select, Skeleton, Text, Title } from 'components/uiKit'
import { getLkGeneralKey, getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'

import { TicketItemHeader } from './components/TicketItemHeader/TicketItemHeader'
import { TicketKeys, ticketStatusOptions, ticketTypeOptions } from './data'
import { TicketStatus, TicketType } from './types'

import noTicketImg from 'assets/img/lk-section/Ticket/no-ticket.png'
import s from './TicketPage.module.scss'

const getKey = (key: string) => `${TicketKeys.ticketPage}.${key}`

const TicketPage = observer(() => {
  const { viewTicketUiStore: uiStore } = useStore()

  const { t } = useTranslation()
  const { isTablet, isMobile } = useResize()

  const isEmpty = uiStore.ticketList.length === 0
  const showAllOption = { value: null, label: t(getSharedKey('all')) }

  useAsyncEffect(async () => {
    if (uiStore.ticketList.length === 0) {
      await uiStore.getTicketList()
    }
  }, [])

  const paginationChange = (page: number, pageSize: number) => {
    if (pageSize !== uiStore.pageSize && uiStore.page !== 1) {
      uiStore.setPage(1)
    } else {
      uiStore.setPage(page)
    }

    uiStore.setPageSize(pageSize)

    void uiStore.getTicketList()
  }

  const changeSearchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value)

    if (e.target.value === '') {
      uiStore.setSearchTrigger(!uiStore.searchTrigger)
    }
  }

  return (
    <ContainerLK>
      <Padding py={32} className={s.TicketPage}>
        <Flex justify="space-between" align={isTablet ? 'flex-start' : 'center'} vertical={isTablet} gap={32}>
          <Title>{t(getLkGeneralKey('Тикеты'))}</Title>
        </Flex>

        <Margin mt={20} mb={32}>
          <Flex justify="space-between" align="end" wrap gap={16}>
            <Flex gap={16} align="end" wrap>
              <Flex style={{ width: isMobile ? '100%' : 'auto' }}>
                <Search
                  allowClear
                  placeholder={`${t(getSharedKey('search'))}...`}
                  value={uiStore.search}
                  onChange={changeSearchHandler}
                  onSearch={() => uiStore.setSearchTrigger(!uiStore.searchTrigger)}
                />
              </Flex>

              <Flex vertical gap={4} style={{ width: isMobile ? '100%' : 150 }}>
                <Text strong>{t(getSharedKey('status'))}</Text>
                <Select
                  value={uiStore.status}
                  onChange={(value: TicketStatus) => uiStore.setStatus(value)}
                  options={[
                    showAllOption,
                    ...ticketStatusOptions.map((ticket) => ({ ...ticket, label: t(getKey(ticket.label)) })),
                  ]}
                />
              </Flex>

              <Flex vertical gap={4} style={{ width: isMobile ? '100%' : 250 }}>
                <Text strong>{t(getSharedKey('topic'))}</Text>
                <Select
                  value={uiStore.ticketType}
                  onChange={(value: TicketType) => uiStore.setTicketType(value)}
                  options={[
                    showAllOption,
                    ...ticketTypeOptions.map((ticket) => ({ ...ticket, label: t(getKey(ticket.label)) })),
                  ]}
                />
              </Flex>
            </Flex>

            <Pagination
              current={uiStore.page}
              onChange={paginationChange}
              pageSize={uiStore.pageSize}
              total={uiStore.ticketStore.totalCount}
              responsive
            />
          </Flex>
        </Margin>

        <Skeleton loading={uiStore.ticketStore.loading}>
          {isEmpty ? (
            <Flex className={s.emptyBlock} vertical justify="center" align="center" gap={8}>
              <img src={noTicketImg} alt="no-ticket" className={s.emptyImg} />

              <Title type="secondary" level={4}>
                {uiStore.emptyFilters
                  ? t(getKey('Обращений в поддержку не было'))
                  : t(getSharedKey('emptySearchResult'))}
              </Title>
            </Flex>
          ) : (
            <Flex vertical gap={8}>
              <Flex>
                <Status
                  title={`${t(getSharedKey('amount'))}: ${uiStore.ticketStore.totalCount}`}
                  type={StatusUIType.gray}
                  smallSize
                />
              </Flex>

              {uiStore.ticketList.map((ticket) => (
                <TicketItemHeader key={ticket.id} ticket={ticket} />
              ))}
            </Flex>
          )}
        </Skeleton>
      </Padding>
    </ContainerLK>
  )
})

export default TicketPage

export const TicketPageAsync = lazy(() => import('./TicketPage'))
